import { ProductsAndModalities, MultiStepFormReferenceData, ProvidersForSelectedModality, ServicesForSelectedModality } from '@/constants/BenefitQuoteMockApiData';
import AbstractClass from './BenefitQuotesAbstractServices';
import { AwaitResponse } from '../../utils/MockUtils';

export default class BenefitQuotesServices extends AbstractClass {
  /**
   * Returns a mock list of members, their products and modalities.
   */
  static getMembersProductsAndModalities() {
    return AwaitResponse(ProductsAndModalities);
  }

  /**
   * Returns a mock list of multi-step form related reference data from Kentico.
   */
  static getMultiStepFormReferenceData() {
    return AwaitResponse(MultiStepFormReferenceData);
  }

  /**
   * Returns landing page related reference data from Kentico.
   */
  static getLandingPageReferenceData() {
    const mockData = {
      benefitQuoteInformationText: {
        informationText: `<h2>Check your benefit</h2><p>Find out how much we&#39;ll cover before you get your treatment.</p>\r\n\r\n<h3>On-the-spot claims</h3>\r\n\r\n
            <p>You can swipe your member card at most extras providers and benefits will be paid on the spot.</p>\r\n\r\n
            <p>We pay benefits according to our General claiming conditions (link to claim rules) and the limitations of your cover. 
            Extras providers need to be registered with their professional body and in the case of approved alternative therapies, recognised by the Australian Regional Health Group.</p>\r\n`,
      },
      benefitQuoteCalloutText: {
        informationText: `<h2>Get a benefit quote</h2>\r\n<p>Use our Benefit Quote Calculator to get an estimate for most extras services covered by your membership.</p>`,
      },
      benefitQuoteCalloutIcon: {
        informationText: '~/assets/public/images/icon_add.svg',
      },
    };
    return AwaitResponse(JSON.parse(JSON.stringify(mockData).tokenSwap()));
  }

  /**
   * Returns a mock list of providers from OHI.
   */
  static getProvidersForSelectedModality() {
    return AwaitResponse(ProvidersForSelectedModality);
  }

  /**
   * Returns a mock list of services for the given modality code.
   */
  static getServicesForSelectedModality() {
    return AwaitResponse(ServicesForSelectedModality);
  }

  /**
   * Mock submitting the quote to OHI.
   */
  static submitBenefitQuote() {
    return AwaitResponse({
      benefitQuoteId: '00000001',
      quotationDate: '25/09/2020',
      memberFullName: 'Jude Anthony',
      quoteSuccessFlag: true,
      modality: {
        claimTypeCode: 'CLAIM TYPE',
        claimTypeName: 'Claim name',
        isUnlimited: false,
        annualBenefitLimit: 100,
        consumption: 200,
        totalBenefitsPayable: 150,
      },
      claimMessage: [
        {
          code: 'claim message code',
          externalCode: 'claim message external code',
          message: 'this is claim message',
        },
      ],
      claimLines: [
        {
          serviceDate: '01/05/2020',
          serviceId: '651',
          serviceName: 'Hard coating - single',
          providerFee: 200,
          benefitsPayable: 150,
          benefitsClaimable: 150,
          messageLine: [
            {
              code: 'claim line message line code',
              externalCode: 'claim line message line external code',
              message: 'Claim message goes here - reasons why the claim was rejected goes here.',
            },
          ],
        },
      ],
      provider: {
        providerId: '0000000A',
        lastName: 'Gail Schwartz',
      },
    });
  }

  /**
   * Returns a mock of details for an existing benefit quote.
   */
  static getBenefitQuoteDetails() {
    return AwaitResponse({
      benefitQuoteId: '000000002',
      quotationDate: '25/09/2020',
      // personCode: 'person code',
      memberFullName: 'Jude Anthony',
      quoteSuccessFlag: true,
      modality: {
        claimTypeCode: 'AUDIOLOGY',
        claimTypeName: 'Audiology',
        isUnlimited: true,
        annualBenefitLimit: 0, // because `isUnlimited` is `true`, FE would ignore this but for completion of payload, return `0`
        consumption: 200,
        totalBenefitsPayable: 150,
      },
      claimMessage: [
        {
          code: 'claim message code',
          externalCode: 'claim message external code',
          message: 'this is claim message',
        },
      ],
      claimLines: [
        {
          serviceDate: '01/02/2020',
          serviceId: '9450',
          serviceName: 'Audiology - Subsequent Consultation',
          providerFee: 100.0,
          benefitsPayable: 97.5,
          benefitsClaimable: null,
          messageLine: [
            {
              code: 'claim line message line code 1',
              externalCode: 'claim line message line external code 1',
              message: 'Claim message goes here - reasons why the claim was rejected goes here. 1',
            },
          ],
        },
        {
          serviceDate: '19/03/2020',
          serviceId: '1234',
          serviceName: 'Dental',
          toothId: 'T001',
          providerFee: 100.0,
          benefitsPayable: 50,
          benefitsClaimable: 50,
          messageLine: [
            {
              code: 'claim line message line code 2',
              externalCode: 'claim line message line external code 2',
              message: 'Claim message goes here - reasons why the claim was rejected goes here. 2',
            },
          ],
        },
        {
          serviceDate: '2020-06-07',
          serviceId: '4623',
          serviceName: 'Some other service',
          providerFee: 100.0,
          benefitsPayable: 52.5,
          benefitsClaimable: 97.5,
          messageLine: [
            {
              code: 'claim line message line code 3',
              externalCode: 'claim line message line external code 3',
              message: 'Claim message goes here - reasons why the claim was rejected goes here. 3',
            },
          ],
        },
      ],
      provider: {
        providerId: '0000000A',
        firstName: 'Furtnam',
        middleName: 'Middle',
        lastName: 'Rastnam',
        addressLine1: '11 Exhibition St',
        addressLine2: '',
        addressLine3: '',
        suburb: 'MELBOURNE',
        state: 'VIC',
        postcode: '3000',
      },
    });
  }

  /**
   * Returns a mock list of all benefit quotes from OHI for a given policy.
   * Used for search benefit quotes.
   */
  static search() {
    return AwaitResponse({
      benefitQuoteList: [
        {
          benefitQuoteId: 'cla4018',
          personCode: '2922',
          memberFullName: 'Jude Anthony',
          modalityName: 'Pharmacy Modality',
          quotationDate: '2020-06-01',
          serviceDate: '2023-03-02'
        },
        {
          benefitQuoteId: 'cla4013',
          personCode: '2922',
          memberFullName: "Nick D'Amico",
          modalityName: 'Ante & Postnatal Modality',
          quotationDate: '2020-06-01',
          serviceDate: '2023-03-02'
        },
        {
          benefitQuoteId: 'CLA4015',
          personCode: '2922',
          memberFullName: 'Jude Anthony',
          modalityName: 'Optical Modality',
          quotationDate: '2020-06-07',
          serviceDate: '2023-03-02'
        },
        {
          benefitQuoteId: 'CLA4010',
          personCode: '2922',
          memberFullName: 'Jude Anthony',
          modalityName: 'Optical Modality',
          quotationDate: '2020-06-07',
          serviceDate: '2023-03-02'
        },
        {
          benefitQuoteId: 'CLA4019',
          personCode: '2922',
          memberFullName: 'Jude Anthony',
          modalityName: 'Physiotherapy Modality',
          quotationDate: '2020-06-07',
          serviceDate: '2023-03-02'
        },
        {
          benefitQuoteId: 'CLA4085',
          personCode: '2922',
          memberFullName: 'Jude Anthony',
          modalityName: 'General and Preventative Dental Modality',
          quotationDate: '2020-06-07',
          serviceDate: '2023-03-02'
        },
        {
          benefitQuoteId: 'CLA4012',
          personCode: '2922',
          memberFullName: 'Jude Anthony',
          modalityName: 'General and Preventative Dental Modality',
          quotationDate: '2020-06-07',
          serviceDate: '2023-03-02'
        },
        {
          benefitQuoteId: 'CLA4014',
          personCode: '2922',
          memberFullName: 'Rupeshkumar Kadam',
          modalityName: 'General and Preventative Dental Modality',
          quotationDate: '2020-06-07',
          serviceDate: '2023-03-02'
        },
        {
          benefitQuoteId: 'CLA4055',
          personCode: '2922',
          memberFullName: 'Jude Anthony',
          modalityName: 'General and Preventative Dental Modality',
          quotationDate: '2020-06-07',
          serviceDate: '2023-03-02'
        },
        {
          benefitQuoteId: 'CLA4066',
          personCode: '2922',
          memberFullName: 'Jude Anthony',
          modalityName: 'General and Preventative Dental Modality',
          quotationDate: '2020-06-07',
          serviceDate: '2023-03-02'
        },
        {
          benefitQuoteId: 'CLA40138',
          personCode: '2922',
          memberFullName: "Nick D'Amico",
          modalityName: 'External Breast Prostheses Modality',
          quotationDate: '2020-06-07',
          serviceDate: '2023-03-02'
        },
        {
          benefitQuoteId: 'CLA4090',
          personCode: '2922',
          memberFullName: 'Jude Anthony',
          modalityName: 'External Breast Prostheses Modality',
          quotationDate: '2020-06-07',
          serviceDate: '2023-03-02'
        },
        {
          benefitQuoteId: 'CLA4099',
          personCode: '2922',
          memberFullName: 'Rupeshkumar Kadam',
          modalityName: 'Ambulance Modality',
          quotationDate: '2020-06-07',
          serviceDate: '2023-03-02'
        },
      ],
    });
  }
}
